import { useEffect, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { Spinner, ButtonSpinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { navigate } from '../../lib/router';
import { useSite } from '../../lib/site';

interface DnsRecord { type: string; name: string; value: string; purpose: string; verified: boolean; }
interface DomainState {
  configured: boolean;
  domain?: string;
  status?: string;
  statusMessage?: string;
  errors?: string[];
  primary?: string;
  records?: DnsRecord[];
}

const STATUS_COLORS: Record<string, { dot: string; badge: string; label: string }> = {
  active: { dot: 'bg-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400', label: 'Active' },
  pending_verification: { dot: 'bg-amber-400', badge: 'bg-amber-500/20 text-amber-400', label: 'Pending DNS' },
  pending_ssl: { dot: 'bg-blue-400', badge: 'bg-blue-500/20 text-blue-400', label: 'Issuing SSL' },
  error: { dot: 'bg-red-400', badge: 'bg-red-500/20 text-red-400', label: 'Error' },
  unknown: { dot: 'bg-slate-400', badge: 'bg-slate-500/20 text-slate-400', label: 'Unknown' },
};

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

export function SiteDomain({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading } = useSite(siteId);
  const [state, setState] = useState<DomainState | null>(null);
  const [stateLoading, setStateLoading] = useState<boolean>(true);
  const [input, setInput] = useState<string>('');
  const [adding, setAdding] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  async function loadStatus() {
    setStateLoading(true);
    try {
      const r = await api('/sites/' + siteId + '/domain');
      if (!r.ok) throw new Error('Failed to load domain status');
      setState(await r.json());
    } catch (e) { setState({ configured: false }); console.error(e); }
    finally { setStateLoading(false); }
  }

  useEffect(() => { loadStatus(); }, [siteId]);

  async function addDomain() {
    const domain = input.trim().toLowerCase();
    if (!domain) { showToast('error', 'Error', 'Please enter a domain'); return; }
    if (!DOMAIN_REGEX.test(domain)) { showToast('error', 'Error', 'Invalid domain format. Enter a domain like example.com'); return; }
    setAdding(true);
    try {
      const r = await api('/sites/' + siteId + '/domain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to add domain');
      showToast('success', 'Domain Added', 'Follow the DNS instructions to complete setup');
      setInput('');
      await loadStatus();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to add domain');
    } finally { setAdding(false); }
  }

  async function removeDomain() {
    if (!confirm('Remove this domain? Your site will only be accessible via the subdomain.')) return;
    try {
      const r = await api('/sites/' + siteId + '/domain', { method: 'DELETE' });
      if (!r.ok) { const result = await r.json().catch(() => ({})); throw new Error((result as any).error || 'Failed to remove domain'); }
      showToast('success', 'Domain Removed', 'Your domain has been disconnected');
      await loadStatus();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to remove domain');
    }
  }

  async function refreshDomain() {
    setRefreshing(true);
    try {
      const r = await api('/sites/' + siteId + '/domain/refresh', { method: 'POST' });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to refresh');
      setState(result);
      if (result.status === 'active') showToast('success', 'Domain Verified', 'Your domain is active and SSL is installed');
      else showToast('info', 'DNS Check', result.statusMessage || 'Verification still pending');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to refresh');
    } finally { setRefreshing(false); }
  }

  async function setPrimary(domain: string) {
    try {
      const r = await api('/sites/' + siteId + '/domain/primary', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to set primary domain');
      showToast('success', 'Primary Updated', `${domain} is now the primary domain`);
      await loadStatus();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to set primary');
      await loadStatus();
    }
  }

  // Gate behind canPublish.
  const blocked = site && !site.canPublish;

  return (
    <SiteShell site={site} loading={siteLoading} active="domain">
      {blocked ? (
        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-8 text-center">
          <div class="w-12 h-12 rounded-full bg-dark-700 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3"/></svg>
          </div>
          <h2 class="text-lg font-semibold text-slate-100 mb-2">Custom domains need a paid plan</h2>
          <p class="text-slate-400 text-sm mb-4">Connect your own domain once this site is on a paid plan.</p>
          <a href={`/sites/${siteId}/billing`} onClick={(e: MouseEvent) => { e.preventDefault(); navigate(`/sites/${siteId}/billing`); }} class="inline-block text-cyan-400 hover:text-cyan-300 text-sm">Go to Billing</a>
        </div>
      ) : (
        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100 mb-4">Custom Domain</h2>
          {stateLoading ? (
            <div class="flex items-center justify-center py-4"><Spinner size="sm" /></div>
          ) : state?.configured && state.domain ? (
            <div class="bg-dark-900 rounded-lg p-4 border border-slate-700/30">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <span class={`w-2 h-2 rounded-full ${(STATUS_COLORS[state.status || 'unknown'] || STATUS_COLORS.unknown).dot}`}></span>
                  <span class="text-sm font-medium text-slate-200">{state.domain}</span>
                </div>
                <span class={`text-xs px-2 py-0.5 rounded ${(STATUS_COLORS[state.status || 'unknown'] || STATUS_COLORS.unknown).badge}`}>
                  {(STATUS_COLORS[state.status || 'unknown'] || STATUS_COLORS.unknown).label}
                </span>
              </div>
              <p class="text-xs text-slate-400 mb-4">{(state.errors && state.errors.length ? state.errors.join('. ') : state.statusMessage) || 'Checking status…'}</p>
              <div class="mb-4">
                <p class="text-xs text-slate-500 font-medium mb-2">Primary domain (non-primary redirects here):</p>
                <div class="space-y-1.5">
                  <label class="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded hover:bg-dark-700 transition-colors">
                    <input type="radio" name="primary" checked={(state.primary || state.domain) === state.domain} onChange={() => setPrimary(state.domain!)} class="accent-cyan-500" />
                    <span class="text-xs text-slate-300">{state.domain}</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded hover:bg-dark-700 transition-colors">
                    <input type="radio" name="primary" checked={(state.primary || state.domain) === 'www.' + state.domain} onChange={() => setPrimary('www.' + state.domain!)} class="accent-cyan-500" />
                    <span class="text-xs text-slate-300">www.{state.domain}</span>
                  </label>
                </div>
              </div>
              <div class="space-y-3 mb-4">
                <p class="text-xs text-slate-500 font-medium">Add these DNS records at your domain registrar:</p>
                {(state.records || []).length === 0 ? (
                  <p class="text-xs text-slate-500">No DNS records to display</p>
                ) : state.records!.map((rec) => (
                  <div class="bg-dark-800 rounded p-2.5 text-xs">
                    <div class="flex items-center justify-between mb-1.5">
                      <div class="flex items-center gap-1.5">
                        <span class="font-mono font-medium text-cyan-400">{rec.type}</span>
                        <span class="text-slate-500">{rec.purpose}</span>
                      </div>
                      {rec.verified ? (
                        <svg class="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                      ) : (
                        <svg class="w-3 h-3 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      )}
                    </div>
                    <div class="space-y-1">
                      <div class="flex gap-2"><span class="text-slate-500 w-12 shrink-0">Name:</span><code class="text-slate-300 break-all">{rec.name}</code></div>
                      <div class="flex gap-2"><span class="text-slate-500 w-12 shrink-0">Value:</span><code class="text-slate-300 break-all select-all">{rec.value}</code></div>
                    </div>
                  </div>
                ))}
              </div>
              <div class="flex gap-2">
                <button type="button" onClick={refreshDomain} disabled={refreshing} class="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm font-medium text-slate-200 hover:bg-dark-600 transition-colors disabled:opacity-60">
                  {refreshing ? <><ButtonSpinner /> Checking…</> : <>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                    Check DNS
                  </>}
                </button>
                <button type="button" onClick={removeDomain} class="px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">Remove</button>
              </div>
            </div>
          ) : (
            <>
              <p class="text-sm text-slate-400 mb-3">Connect your own domain to make your site live at your custom URL.</p>
              <div class="flex gap-2">
                <input
                  type="text" placeholder="example.com"
                  value={input}
                  onInput={(e: Event) => setInput((e.currentTarget as HTMLInputElement).value)}
                  onKeyPress={(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                  class="flex-1 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                />
                <button type="button" onClick={addDomain} disabled={adding} class="px-4 py-2 bg-cyan-500 text-dark-900 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors disabled:opacity-60">
                  {adding ? 'Adding…' : 'Add Domain'}
                </button>
              </div>
              <p class="text-xs text-slate-500 mt-1.5">Enter your root domain (e.g., example.com). Both example.com and www.example.com will work.</p>
            </>
          )}
        </div>
      )}
    </SiteShell>
  );
}
